import { Button, Checkbox, DialogV2, Loader, Typography } from '@recruit-robin/robin-theme/web-components';
import {
  Indeed,
  Linkedin,
  Mail_01,
  RobinIconMonoFulfilled,
  Werknl,
  Whatsapp,
  XClose,
} from '@recruit-robin/robin-theme/web-icons';
import { useContext, useState } from 'react';

import type { Candidate } from 'model/Candidate/Candidate';
import type { OutreachMethods } from 'shared/legacy/contexts/CandidateContext/types';

import { useOutreach } from 'shared/legacy/contexts/CandidateContext/outreach';
import { AccountStatus } from 'shared/legacy/contexts/CandidateContext/CandidateEmailSettingsContext.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { useOauthNylasStatusQuery } from 'shared/legacy/hooks/useOauthNylasStatusQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogConfig } from 'stores/global-dialogs/hooks/useGlobalDialogConfig';
import { OutreachType } from 'types/outreach';
import { useStyles } from './FollowUpAlternativeChannelsScreen.styles';

export type FollowUpAlternativeChannelsScreenProps = {
  candidate: Candidate;
  onFinish: (params?: { hasFollowUp: boolean }) => Promise<{ hasError: boolean }>;
  isLoading?: boolean;
};

export const FollowUpAlternativeChannelsScreen = ({
  candidate,
  onFinish,
  isLoading = false,
}: FollowUpAlternativeChannelsScreenProps) => {
  const { dictionary } = useContext(LocalizationContext);
  const classes = useStyles();

  const [isLoadingType, setIsLoadingType] = useState<OutreachType | null>(null);

  const { availableOutreachMethods } = useOutreach(candidate);
  const outreachSettings = useOutreachSettings();

  const campaignPhoneReachOutConfig = useGlobalDialogConfig(GlobalDialogType.CampaignPhoneReachOut);

  const handleToggleDontShowThisNextTime = () => {
    campaignPhoneReachOutConfig.set({ skipFollowUp: !campaignPhoneReachOutConfig.config.skipFollowUp });
  };

  const { open } = useGlobalDialog();
  const { data: { email: emailStatus } = {} } = useOauthNylasStatusQuery();
  const { isFeatureToggleEnabled } = useFeatureToggles();
  const shouldEnableEmailWithoutNylasConnection = isFeatureToggleEnabled(
    (f) => f.ENABLE_EMAIL_WITHOUT_NYLAS_CONNECTION,
  );

  const handleOpenOutreachModal = (outreachType: OutreachType) => {
    setIsLoadingType(outreachType);

    const shouldOpenConnectEmailDialog =
      outreachType === OutreachType.Email &&
      emailStatus !== AccountStatus.Valid &&
      !shouldEnableEmailWithoutNylasConnection;

    if (shouldOpenConnectEmailDialog) {
      setIsLoadingType(null);
      open(GlobalDialogType.ShouldConnectEmail);
      return;
    }

    onFinish({ hasFollowUp: true }).then(({ hasError }) => {
      setIsLoadingType(null);

      if (hasError) {
        return;
      }

      open(GlobalDialogType.CandidateOutreach, { payload: { candidate, outreachType } });
    });
  };

  return (
    <>
      <DialogV2.Header customCss={classes.header}>
        <Typography variant={(v) => v.heading[5]} color={(c) => c.neutral.variant[80]}>
          {dictionary.alternativeChannels}
        </Typography>

        <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[50]}>
          {dictionary.alternativeChannelsDescription}
        </Typography>
      </DialogV2.Header>

      <DialogV2.Body customCss={classes.body}>
        {availableOutreachMethods.includes('shouldEnableRJCommHub') && (
          <div
            css={[classes.button, classes.buttonRobinJr]}
            onClick={() => handleOpenOutreachModal(OutreachType.RJCommHub)}>
            {isLoadingType === OutreachType.RJCommHub ? (
              <Loader size={16} color={(c) => c.neutral.variant} />
            ) : (
              <RobinIconMonoFulfilled size={16} />
            )}
            <Typography variant={(v) => v.supporting.helper}>{dictionary.activateRobinJrTitle}</Typography>
          </div>
        )}

        {availableOutreachMethods.map((method) => {
          const outreachMethod = outreachSettings[method];

          if (!outreachMethod) return null;

          return (
            <div key={method} css={classes.button} onClick={() => handleOpenOutreachModal(outreachMethod.type)}>
              {isLoadingType === outreachMethod.type ? (
                <Loader size={16} color={(c) => c.neutral.variant} />
              ) : (
                outreachMethod.icon
              )}
              <Typography variant={(v) => v.supporting.helper}>{outreachMethod.label}</Typography>
            </div>
          );
        })}
      </DialogV2.Body>

      <DialogV2.Footer customCss={classes.footer}>
        <Checkbox
          label={
            <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[50]}>
              {dictionary.dontShowThisNextTime}
            </Typography>
          }
          checked={campaignPhoneReachOutConfig.config.skipFollowUp}
          onClick={handleToggleDontShowThisNextTime}
        />

        <Button
          label={dictionary.close}
          onClick={() => onFinish()}
          style="filled"
          type="warning"
          startIcon={isLoading ? <Loader size={16} color={(c) => c.tertiary.lime} /> : <XClose size={16} />}
          disabled={isLoading}
        />
      </DialogV2.Footer>
    </>
  );
};

const useOutreachSettings = () => {
  const { dictionary } = useContext(LocalizationContext);

  const settings: Partial<Record<keyof OutreachMethods, { icon: React.ReactNode; label: string; type: OutreachType }>> =
    {
      shouldEnableEmail: { icon: <Mail_01 size={16} />, label: dictionary.sendEmail, type: OutreachType.Email },
      shouldEnableWhatsapp: {
        icon: <Whatsapp size={16} />,
        label: dictionary.sendWhatsApp,
        type: OutreachType.Whatsapp,
      },
      shouldEnableLinkedin: {
        icon: <Linkedin size={16} />,
        label: dictionary.sendLinkedin,
        type: OutreachType.Linkedin,
      },
      shouldEnableLinkedinPro: {
        icon: <Linkedin size={16} />,
        label: dictionary.sendInMail,
        type: OutreachType.LinkedinInmail,
      },
      shouldEnableIndeed: { icon: <Indeed size={16} />, label: dictionary.sendIndeed, type: OutreachType.Indeed },
      shouldEnableWerknl: { icon: <Werknl size={16} />, label: dictionary.sendWerknl, type: OutreachType.Werknl },
    };

  return settings;
};
